import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, input } from '@angular/core';
import { FormArray, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog'
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReportService } from 'src/app/services/report.service';
import { Subject } from 'rxjs';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';
import { StateCounty } from 'src/app/models/stateCounty.model';

@Component({
  selector: 'app-view-watershed-condition',
  templateUrl: './view-watershed-condition.component.html',
  styleUrls: ['./view-watershed-condition.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})


export class ViewWatershedConditionComponent implements OnInit, AfterViewInit {

  @Output() loaded = new EventEmitter();
  faWarning = faWarning;
  faCircleXmark = faCircleXmark as IconProp;

  report = input.required<any>();
  forests = []
  saveSubject = new Subject<any>();


  form = this.formBuilder.group({
    acresBurned: this.formBuilder.array([]),
    erosion_potential: [""],
    debris_potential: [""],
    vegetative_recovery: [""],
    hydrolic_response: [""],

  });
  get acresBurned() {
    return this.form.get("acresBurned") as FormArray;
  }

  constructor(
    public dialog: MatDialog,
    private readonly titleService: Title,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    public readonly reportService: ReportService,
    public readonly toast: ConfirmationToastComponent,
  ) {

  }

  ngOnInit(): void {
    this.forests = this.report().assessment.forest_name.split(",")
    this.buildTableForms()
    this.form.patchValue({
      erosion_potential: this.report().erosion_potential ?? "",
      debris_potential: this.report().debris_potential ?? "",
      vegetative_recovery: this.report().vegetative_recovery ?? "",
      hydrolic_response: this.report().hydrolic_response ?? "",
    })
  }

  ngAfterViewInit(): void {
    this.loaded.emit(3);
  }

  buildTableForms(): void {
    let states = this.report().stateCounties.filter((state: StateCounty) => state?.state_name != null).map((state: StateCounty) => state?.state_name)
    states = [...new Set(states)]; //remove duplicates

    let burnData = [
      {
        ownership: "Private",
        acres_burned: 0,
        percent_burned: 0,
        burn_acres_developed: 0,
        burn_acres_unburned: 0,
        burn_acres_low: 0,
        burn_acres_moderate: 0,
        burn_acres_high: 0,
        report_soil_burn_severity_id: -1
      },
      {
        ownership: "Other Federal",
        acres_burned: 0,
        percent_burned: 0,
        burn_acres_developed: 0,
        burn_acres_unburned: 0,
        burn_acres_low: 0,
        burn_acres_moderate: 0,
        burn_acres_high: 0,
        report_soil_burn_severity_id: -1
      },
    ]

    for (let state of states) {
      burnData.unshift(
        {
          ownership: "State of " + state,
          acres_burned: 0,
          percent_burned: 0,
          burn_acres_developed: 0,
          burn_acres_unburned: 0,
          burn_acres_low: 0,
          burn_acres_moderate: 0,
          burn_acres_high: 0,
          report_soil_burn_severity_id: -1
        }
      )
    }

    for (let forest of this.forests) {
      burnData.unshift(
        {
          ownership: forest,
          acres_burned: 0,
          percent_burned: 0,
          burn_acres_developed: 0,
          burn_acres_unburned: 0,
          burn_acres_low: 0,
          burn_acres_moderate: 0,
          burn_acres_high: 0,
          report_soil_burn_severity_id: -1
        }
      )
    }


    for (let item of this.report().soil_burn_severities) {
      let burnDataItem = burnData.find((x: any) => x.ownership == item.ownership)
      if (burnDataItem) {
        burnDataItem.acres_burned = item.acres_burned;
        burnDataItem.percent_burned = item.percent_burned;
        burnDataItem.burn_acres_developed = item.burn_acres_developed;
        burnDataItem.burn_acres_unburned = item.burn_acres_unburned;
        burnDataItem.burn_acres_low = item.burn_acres_low;
        burnDataItem.burn_acres_moderate = item.burn_acres_moderate;
        burnDataItem.burn_acres_high = item.burn_acres_high;
        burnDataItem.report_soil_burn_severity_id = item.report_soil_burn_severity_id;
      }
    }
    for (let data of burnData) {
      this.acresBurned.push(this.formBuilder.group({
        ownership: data.ownership,
        acres_burned: data.acres_burned,
        percent_burned: data.percent_burned,
        burn_acres_developed: data.burn_acres_developed,
        burn_acres_unburned: data.burn_acres_unburned,
        burn_acres_low: data.burn_acres_low,
        burn_acres_moderate: data.burn_acres_moderate,
        burn_acres_high: data.burn_acres_high,
        report_soil_burn_severity_id: data.report_soil_burn_severity_id,
      }))
    }
  }

  sumAcresBurned(level: string, sbs: boolean) {
    let total = 0
    this.acresBurned.value.forEach((item: any, index: number) => {
      if (!sbs || (!item.ownership.includes('State') && index != (this.acresBurned.value?.length - 2) && index != (this.acresBurned.value?.length - 1)))
        total += +item[level]
    })
    return total
  }

  categoryTotal(ownership: string) {
    let total = 0;
    this.acresBurned.value.forEach((item: any, index: number) => {
      if ((ownership == 'nfs' && index != (this.acresBurned.value?.length - 2) && index != (this.acresBurned.value?.length - 1))
        || item.ownership.includes(ownership)
        || ownership == 'total')
        total = total + item['burn_acres_developed'] + item['burn_acres_unburned'] + item['burn_acres_low'] + item['burn_acres_moderate'] + item['burn_acres_high'];
    })
    return total;
  }

  customATOB(s: string): string {
    try {
      return atob(s)
    } catch (err) {
      if (err) {
        return s
      }
    }
    return s
  }
}
